import {
  BoardMeetingState,
  boardMeetingStateTimeline,
} from "@boark/shared/types/board-meeting";
import { BoardMeetingUpdate } from "@boark/shared/types/board-meeting-update";
import { BoardMeetingWithDetails } from "@boark/shared/types/board-meeting-with-details";
import {
  BoardMeetingError,
  canConfirmBoardMeeting,
  canEditBoardMeetingAssignments,
  canEditBoardMeetingAttendance,
  hasBoardMeetingConfirmed,
} from "@boark/shared/utils/board-meeting";
import { faCheck } from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";

import { useBoardMeetingMutation } from "../queries/board-meeting";
import { useBlockedReason } from "../services/board-meeting-locale";
import {
  formatUserName,
  setBoardMeetingConfirmation,
  updateBoardMeeting,
} from "../services/board-meeting";
import { useLocale } from "../stores/locale";
import { formatDateTime } from "../utils/date";
import { Button } from "./button";

function hasReached(
  meeting: BoardMeetingWithDetails,
  state: BoardMeetingState,
) {
  return (
    boardMeetingStateTimeline.indexOf(meeting.state) >=
    boardMeetingStateTimeline.indexOf(state)
  );
}

export function BoardMeetingPeople({
  meeting,
  authUserId,
}: {
  meeting: BoardMeetingWithDetails;
  authUserId?: string;
}) {
  const { locale: t } = useLocale();
  const blockedReason = useBlockedReason();

  const update = useBoardMeetingMutation(
    meeting.id,
    (changes: BoardMeetingUpdate) => updateBoardMeeting(meeting.id, changes),
  );
  const confirm = useBoardMeetingMutation(meeting.id, (confirmed: boolean) =>
    setBoardMeetingConfirmation(meeting.id, confirmed),
  );

  const assignmentsEditable = canEditBoardMeetingAssignments(
    meeting,
    authUserId,
  );
  const attendanceEditable = canEditBoardMeetingAttendance(meeting, authUserId);
  const showRoles = hasReached(meeting, BoardMeetingState.OPENED);
  const showConfirmations = hasReached(meeting, BoardMeetingState.REVIEW);

  const attendedIds = meeting.attended.map((user) => user.id);
  const verifierIds = meeting.verifiers.map((user) => user.id);

  const toggle = (ids: string[], id: string) =>
    ids.includes(id) ? ids.filter((other) => other !== id) : [...ids, id];

  const confirmationOf = (userId: string) =>
    meeting.confirmations.find(
      (confirmation) => confirmation.user.id === userId,
    );

  const canConfirm = canConfirmBoardMeeting(meeting, authUserId);
  const hasConfirmed = hasBoardMeetingConfirmed(meeting, authUserId);

  return (
    <div className="flex flex-col gap-3">
      <p className="m-0 text-sm">
        <span className="font-medium">{t.boardMeeting.caller}:</span>{" "}
        {formatUserName(meeting.caller)}
        {meeting.caller.id === authUserId && ` (${t.boardMeeting.you})`}
      </p>
      {meeting.called.length === 0 ? (
        <p className="text-sm text-gray-500 m-0">
          {t.boardMeeting.calledEmpty}
        </p>
      ) : (
        <div className="overflow-x-auto">
          <table className="w-full text-sm border-collapse">
            <thead>
              <tr className="text-left text-gray-500 border-b border-gray-200">
                <th className="py-1 pr-3 font-medium">
                  {t.boardMeeting.called}
                </th>
                {showRoles && (
                  <>
                    <th className="py-1 px-2 font-medium text-center">
                      {t.boardMeeting.attended}
                    </th>
                    <th className="py-1 px-2 font-medium text-center">
                      {t.boardMeeting.secretary}
                    </th>
                    <th className="py-1 px-2 font-medium text-center">
                      {t.boardMeeting.verifiers}
                    </th>
                  </>
                )}
                {showConfirmations && (
                  <th className="py-1 pl-2 font-medium">
                    {t.boardMeeting.review}
                  </th>
                )}
              </tr>
            </thead>
            <tbody>
              {meeting.called.map((user) => {
                const isVerifier = verifierIds.includes(user.id);
                const isSecretary = meeting.secretary?.id === user.id;
                const confirmation = confirmationOf(user.id);
                return (
                  <tr key={user.id} className="border-b border-gray-100">
                    <td className="py-1 pr-3">
                      {formatUserName(user)}
                      {user.id === authUserId && (
                        <span className="text-gray-500">
                          {" "}
                          ({t.boardMeeting.you})
                        </span>
                      )}
                    </td>
                    {showRoles && (
                      <>
                        <td className="py-1 px-2 text-center">
                          <input
                            aria-label={`${t.boardMeeting.attended}: ${formatUserName(user)}`}
                            checked={attendedIds.includes(user.id)}
                            disabled={!attendanceEditable || update.isPending}
                            type="checkbox"
                            onChange={() =>
                              update.mutate({
                                attendedIds: toggle(attendedIds, user.id),
                              })
                            }
                          />
                        </td>
                        <td className="py-1 px-2 text-center">
                          <input
                            aria-label={`${t.boardMeeting.secretary}: ${formatUserName(user)}`}
                            checked={isSecretary}
                            disabled={
                              !assignmentsEditable ||
                              update.isPending ||
                              isVerifier
                            }
                            name="secretary"
                            title={
                              isVerifier
                                ? blockedReason(
                                    BoardMeetingError.SecretaryIsVerifier,
                                  )
                                : undefined
                            }
                            type="radio"
                            onChange={() =>
                              update.mutate({ secretaryId: user.id })
                            }
                          />
                        </td>
                        <td className="py-1 px-2 text-center">
                          <input
                            aria-label={`${t.boardMeeting.verifiers}: ${formatUserName(user)}`}
                            checked={isVerifier}
                            disabled={
                              !assignmentsEditable ||
                              update.isPending ||
                              isSecretary
                            }
                            title={
                              isSecretary
                                ? blockedReason(
                                    BoardMeetingError.SecretaryIsVerifier,
                                  )
                                : undefined
                            }
                            type="checkbox"
                            onChange={() =>
                              update.mutate({
                                verifierIds: toggle(verifierIds, user.id),
                              })
                            }
                          />
                        </td>
                      </>
                    )}
                    {showConfirmations && (
                      <td className="py-1 pl-2">
                        {isVerifier &&
                          (confirmation ? (
                            <span className="text-green-700">
                              <FontAwesomeIcon
                                className="mr-1"
                                icon={faCheck}
                              />
                              {t.boardMeeting.confirmedAt.replace(
                                "{date}",
                                formatDateTime(confirmation.confirmedAt, false),
                              )}
                            </span>
                          ) : (
                            <span className="text-gray-500">
                              {t.boardMeeting.notConfirmed}
                            </span>
                          ))}
                      </td>
                    )}
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
      {canConfirm && (
        <div className="flex justify-end">
          <Button
            loading={confirm.isPending}
            severity={hasConfirmed ? "secondary" : "primary"}
            onClick={() => confirm.mutate(!hasConfirmed)}
          >
            {hasConfirmed
              ? t.boardMeeting.revokeConfirmation
              : t.boardMeeting.confirm}
          </Button>
        </div>
      )}
    </div>
  );
}
